// ============================================================
// useCaseLive.ts — (E-sign fixes B; the first slice of roadmap D12) keep a
// case screen fresh without a page reload.
//
// Rides the EXISTING presence WebSocket (services/presence.ts) — no second
// connection. Calls `onChange` (coalesced: one call per ~150 ms burst) when:
//   • a 'case-changed' message arrives for THIS case with one of `topics`
//     (an empty `topics` list = any topic). The message carries no data — the
//     consumer re-reads through its normal REST endpoint;
//   • the socket RE-connects (anything sent while it was down is lost);
//   • fallback poll: every `pollMs` (20 s) while `pollWhile` is true AND the
//     tab is visible — and once on becoming visible again — for when the
//     socket is down or a change happened that wasn't announced.
// Generic on purpose: nothing about e-signature in here.
// ============================================================
import { useEffect, useRef } from 'react'
import { presence } from '../services/presence'

export interface CaseLiveOptions {
  // The fallback poll interval (default 20 000 ms; 0 = no polling).
  pollMs?: number
  // Poll only while this is true (e.g. something is still in progress).
  pollWhile?: boolean
  // false = do nothing at all (default true).
  enabled?: boolean
  // The coalescing window (default 150 ms).
  debounceMs?: number
}

const visible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden'

export const useCaseLive = (
  caseId: string | null | undefined,
  topics: readonly string[],
  onChange: () => void,
  opts: CaseLiveOptions = {},
): void => {
  const cbRef = useRef(onChange)
  cbRef.current = onChange
  const enabled = opts.enabled !== false && !!caseId
  const pollMs = opts.pollMs ?? 20_000
  const pollWhile = !!opts.pollWhile
  const delay = opts.debounceMs ?? 150
  const topicKey = [...topics].sort().join('|')

  // One coalescing timer shared by every input (message, reconnect, poll).
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const queueRef = useRef<() => void>(() => undefined)
  queueRef.current = () => {
    if (timer.current) return
    timer.current = setTimeout(() => { timer.current = null; cbRef.current() }, delay)
  }
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); timer.current = null } }, [])

  // The socket: this case's messages + every re-connect.
  useEffect(() => {
    if (!enabled) return undefined
    const wanted = topicKey ? topicKey.split('|') : []
    const offs = [
      presence.subscribeCaseChanged((e) => {
        if (e.caseId !== caseId) return
        if (wanted.length && !e.topics.some((t) => wanted.includes(t))) return
        queueRef.current()
      }),
      presence.subscribeReconnected(() => queueRef.current()),
    ]
    return () => { offs.forEach((off) => off()) }
  }, [enabled, caseId, topicKey])

  // The fallback poll (visible tab only) + once on coming back to the tab.
  useEffect(() => {
    if (!enabled || !pollWhile || !(pollMs > 0)) return undefined
    const tick = setInterval(() => { if (visible()) queueRef.current() }, pollMs)
    const onVis = (): void => { if (visible()) queueRef.current() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(tick); document.removeEventListener('visibilitychange', onVis) }
  }, [enabled, pollWhile, pollMs])
}

export default useCaseLive
